import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  ButtonGroup,
  InlineStack,
  LegacyCard as Card,
  Select,
  Text,
} from "@shopify/polaris";
import { CartesianGrid, Line, LineChart, Tooltip, XAxis, YAxis } from "recharts";

import {
  formatPerformanceCurrencyK,
  PERFORMANCE_CHART_DATA,
  PERFORMANCE_METRIC_OPTIONS,
} from "app/dashboard/modules/operator/dashboardConfig";

import PerformanceTable from "./performanceTable";

const formatYAxis = (value) => formatPerformanceCurrencyK(value);

const MarketplacePerformance = ({ chartData = PERFORMANCE_CHART_DATA }) => {
  const [viewMode, setViewMode] = useState("graph");
  const [metric, setMetric] = useState(PERFORMANCE_METRIC_OPTIONS[0].value);
  const chartRef = useRef(null);
  const [chartWidth, setChartWidth] = useState(800);

  useEffect(() => {
    const updateWidth = () => {
      if (chartRef.current) {
        setChartWidth(chartRef.current.offsetWidth);
      }
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, [viewMode]);

  return (
    <Card>
      <div className="operator-dashboard-performance">
        <InlineStack align="space-between" blockAlign="center">
          <Text as="h2" variant="headingMd" fontWeight="semibold">
            Marketplace Performance
          </Text>
          <InlineStack gap="300" blockAlign="center">
            <div className="operator-dashboard-metric-select">
              <Select
                label="Metric"
                labelHidden
                options={PERFORMANCE_METRIC_OPTIONS}
                value={metric}
                onChange={setMetric}
              />
            </div>
            <ButtonGroup variant="segmented">
              <Button pressed={viewMode === "graph"} onClick={() => setViewMode("graph")}>
                Graph
              </Button>
              <Button pressed={viewMode === "table"} onClick={() => setViewMode("table")}>
                Table
              </Button>
            </ButtonGroup>
          </InlineStack>
        </InlineStack>

        <div className="operator-dashboard-performance-body" ref={chartRef}>
          {viewMode === "graph" ? (
            <LineChart
              width={chartWidth}
              height={320}
              data={chartData}
              margin={{ top: 16, right: 16, left: 8, bottom: 8 }}
            >
              <CartesianGrid stroke="#e5e7eb" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 12, fill: "#6b7280" }} axisLine={false} tickLine={false} />
              <YAxis
                tickFormatter={formatYAxis}
                tick={{ fontSize: 12, fill: "#6b7280" }}
                axisLine={false}
                tickLine={false}
                domain={[4000, 8000]}
                ticks={[4000, 4500, 5000, 5500, 6000, 6500, 7000, 7500]}
              />
              <Tooltip formatter={(val) => [`$${Number(val).toLocaleString()}`, metric.toUpperCase()]} />
              <Line
                type="monotone"
                dataKey={metric}
                stroke="#111827"
                strokeWidth={2}
                dot={{ r: 4, fill: "#111827", strokeWidth: 0 }}
                activeDot={{ r: 5, fill: "#111827" }}
              />
            </LineChart>
          ) : (
            <PerformanceTable rows={chartData} />
          )}
        </div>
      </div>
    </Card>
  );
};

MarketplacePerformance.propTypes = {
  chartData: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string.isRequired,
      gmv: PropTypes.number.isRequired,
      revenue: PropTypes.number.isRequired,
      operatingCosts: PropTypes.number.isRequired,
      orders: PropTypes.number.isRequired,
      avgOrderValue: PropTypes.number.isRequired,
    })
  ),
};

export default MarketplacePerformance;
